import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useMediaQuery } from 'react-responsive';
import { Badge, Button, Column, Container, Icon, Row } from '@pella/sash-framework';
import { Layout, SEO, Image, CTA, Video } from '../../components';
import { useBranchShowrooms } from '../../hooks/use-branch-showrooms';
import { isValidArray } from '../../components/utilities/validations';
import { getSessionStorage } from '../../components/utilities/functions';

import CardSection from '../../components/common/card-section';
import DynamicComponent from '../../components/common/dynamic-component';
import NearbyShowrooms from '../../components/template/branch-showroom/nearby-showrooms';
import RTASimpleForm from '../../components/common/forms/rta-simple-form';
import LocalNavTop from '../../components/common/local-nav-top';
import BranchHero from '../../components/common/branch-hero';
import FinancingBlock from '../../components/common/financing-block';
import ElfSightEmbed from '../../components/common/elfsight-embed';
import BranchCollectionSD from '../../components/common/structured-data/branch-collection';
import PromoBanner from '../../components/common/promo-banner';
import VisualJourneyModal from '../../components/page/support/visual-journey-modal';
import HoverBannerLarge from '../../components/third-party/hover/large-banner';

const BranchHomepage = (props) => {
	const pageData = props.data.contentstackBranchHomepage;
	const combinedShowrooms = [...props.data.showroomsByBranch?.nodes, ...props.data.showroomsByStoreNumber?.nodes];
	const { showrooms, error } = useBranchShowrooms(pageData.branch_data?.[0]?.branch_retail, combinedShowrooms, pageData.branch_data?.[0]?.additional_showrooms);
	const branchData = isValidArray(pageData.branch_data) ? pageData.branch_data[0] : {};
	const isDesktop = useMediaQuery({ minWidth: 1024 });
	const isTablet = useMediaQuery({ minWidth: 768 });
	const [isVisualJourneyOpen, setIsVisualJourneyOpen] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const toggleList = () => {
		setExpanded((prev) => !prev);
	};

	useEffect(() => {
		if (getSessionStorage('journeyOptOut')) return;

		const visualJourneyLinks = document.querySelectorAll('.cards-visual-journey a[href="/support/request-visit/"]');
		if (visualJourneyLinks?.length > 0) {
			visualJourneyLinks.forEach((link) => {
				link.addEventListener('click', (event) => {
					event.preventDefault();
					setIsVisualJourneyOpen(true);
				});
			});
		}

		return () => {
			if (visualJourneyLinks?.length > 0) {
				visualJourneyLinks.forEach((link) => {
					link.removeEventListener('click', (event) => {
						event.preventDefault();
						setIsVisualJourneyOpen(true);
					});
				});
			}
		};
	}, []);

	return (
		<Layout
			id='template-BranchHomepage'
			pageTitle={pageData.seo?.title}
		>
			{isValidArray(branchData?.local_nav) && (
				<LocalNavTop
					branchData={branchData}
					linksArray={branchData.local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				>
					{isDesktop && pageData.sponsorship && (
						<div className='branch-sponsorship flex items-center gap-x-2.5'>
							{pageData.sponsorship?.logo?.url && (
								<Image
									data={{ image: pageData.sponsorship.logo }}
									className='h-12 w-[66px] flex-none [&_img]:!object-contain'
								/>
							)}

							{pageData.sponsorship?.description && (
								<div
									dangerouslySetInnerHTML={{ __html: pageData.sponsorship.description }}
									className='max-w-[30ch] flex-1 text-balance text-xs font-semibold tracking-0.25px text-white'
								/>
							)}
						</div>
					)}
				</LocalNavTop>
			)}
			{pageData.hero && (
				<BranchHero
					data={pageData.hero}
					sponsor={pageData.sponsorship}
				/>
			)}
			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.cards != null) {
						return (
							<CardSection
								key={index}
								data={block.cards}
							/>
						);
					}

					if (block.financing != null) {
						return (
							<div
								className='bg-neutral-50 pb-10'
								id={block.financing.anchor_text}
							>
								<Container>
									<FinancingBlock data={branchData?.financing_options?.[0]?.manual_option?.[0]} />
								</Container>
							</div>
						);
					}

					if (block.all_dynamic != null && isValidArray(block.all_dynamic.dynamic)) {
						return (
							<DynamicComponent
								key={index}
								data={block.all_dynamic}
							/>
						);
					}

					if (block.about != null) {
						return (
							<div
								key={index}
								className='branchAbout py-5 xl:py-8'
							>
								<Container>
									<Row
										align='center'
										reverse={block.about.image_position === 'right'}
										verticalGutters
										largeGutters
									>
										{isValidArray(block.about.image) && (
											<Column
												xs={12}
												lg={block.about.image_column}
												className='text-center'
											>
												<div className='relative inline-block'>
													<Image
														data={block.about.image[0]}
														className='lg:mb-0'
													/>

													{isValidArray(block.about.badge) && (
														<Badge
															renderAs='a'
															color='black'
															href={block.about.badge[0].link.href}
															className='absolute left-6 top-6 gap-x-1 xl:bottom-6 xl:left-auto xl:right-6 xl:top-auto'
															variant='rounded'
														>
															{block.about.badge[0].material_icon && (
																<Icon
																	name={block.about.badge[0].material_icon}
																	size={18}
																/>
															)}
															{block.about.badge[0].link.title}
														</Badge>
													)}
												</div>
											</Column>
										)}

										{block.about.video && isValidArray(block.about.video) && (
											<Column>
												<div>
													<Video data={block.about.video[0]} />
												</div>
											</Column>
										)}

										<Column
											xs={12}
											lg='fluid'
										>
											{block.about.heading && (
												<h2
													className='m-0 text-22px font-semibold xl:text-26px'
													dangerouslySetInnerHTML={{ __html: block.about.heading }}
												/>
											)}
											{block.about.description && (
												<div
													className='mt-5 xl:mt-6'
													dangerouslySetInnerHTML={{ __html: block.about.description }}
												/>
											)}
											{isValidArray(block.about.cta) && (
												<CTA
													className='mt-6 xl:mt-7'
													data={block.about.cta[0]}
												/>
											)}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.nearby != null) {
						return (
							<Container
								key={index}
								className='branchNearby py-6 xl:py-12'
							>
								{block.nearby.heading && (
									<h2
										className='mb-3 text-xl font-semibold xl:text-28px'
										dangerouslySetInnerHTML={{ __html: block.nearby.heading }}
									/>
								)}

								<NearbyShowrooms
									locations={showrooms.filter((showroom) => (branchData?.by_state ? showroom.state === branchData?.state?.abbreviation : true))}
									key={showrooms?.length}
									error={error}
									loadMore
								/>
							</Container>
						);
					}

					if (block.rta != null) {
						return (
							<div
								key={index}
								className={`branchRta xl:py-12 ${block.rta.gray_background ? 'bg-neutral-50' : 'bg-white'}`}
							>
								<Container className='py-3'>
									<Row
										largeGutters
										verticalGutters
									>
										<Column
											xs={12}
											md='fluid'
											className='max-md:text-center'
										>
											{block.rta.heading && <h2 className='mb-3 text-22px !leading-tight md:mb-4 md:text-26px xl:text-28px'>{block.rta.heading}</h2>}
											{block.rta.subheading && (
												<div
													className='font-semibold uppercase !leading-tight tracking-1px text-neutral-600 [&_p]:flex [&_p]:items-center max-md:[&_p]:justify-center [&_span]:mr-2'
													dangerouslySetInnerHTML={{ __html: block.rta.subheading }}
												/>
											)}

											{block.rta.description && (
												<>
													<div
														className={`mt-3 md:mt-4 md:text-base xl:text-xl [&_ul]:mt-6 max-md:[&_ul]:inline-block xl:[&_ul]:mt-8 ${expanded ? '[&_li]:flex' : 'md:[&_li:nth-child(n+3)]:hidden'}`}
														dangerouslySetInnerHTML={{ __html: block.rta.description }}
													/>
													<Button
														color='tertiary-alt'
														onClick={toggleList}
														className='mt-4 hidden md:inline-flex'
													>
														{expanded ? 'Read Less' : 'Read More'}
													</Button>
												</>
											)}

											{isTablet && (
												<PromoBanner
													type='component'
													isCollapsible={false}
													bgColor={block.rta.gray_background ? 'bg-neutral-50' : 'bg-white'}
													classes='max-w-[568px] mt-6 xl:mt-7'
												/>
											)}
										</Column>

										<Column
											xs={12}
											md={6}
										>
											<RTASimpleForm />
											{!isTablet && (
												<PromoBanner
													type='component'
													isCollapsible={false}
													bgColor={block.rta.gray_background ? 'bg-neutral-50' : 'bg-white'}
													classes='mt-4'
												/>
											)}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.feeds != null && branchData?.social_id != null) {
						return (
							<div
								key={index}
								id={block.feeds.anchor_text}
								className='block-elfsight-social py-6 md:py-8'
							>
								<Container>
									<ElfSightEmbed elfsightId={branchData.social_id} />
								</Container>
							</div>
						);
					}

					if (block.embed != null) {
						return (
							<div
								key={index}
								className='catInspiration-embed my-10'
							>
								<Container>
									<HoverBannerLarge />
								</Container>
							</div>
						);
					}
				})}

			{branchData?.roc && (
				<Container>
					<p className='text-tiny mb-4 text-neutral'>{branchData.roc}</p>
				</Container>
			)}
			<VisualJourneyModal
				isOpen={isVisualJourneyOpen}
				redirect='/support/request-visit/'
				onClose={() => setIsVisualJourneyOpen(false)}
			/>
			{/* Structured Data */}
			<BranchCollectionSD
				branchData={branchData}
				description={pageData.seo.description}
				url={pageData.url}
				showrooms={isValidArray(showrooms) ? showrooms.filter((showroom) => (branchData?.by_state ? showroom.state === branchData?.state?.abbreviation : true)) : []}
			/>
		</Layout>
	);
};

BranchHomepage.propTypes = {
	data: PropTypes.object,
};

export default BranchHomepage;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackBranchHomepage.seo} />;

export const query = graphql`
	query ($uid: String!, $branch_id: [String], $store_numbers: [String]) {
		contentstackBranchHomepage(id: { eq: $uid }) {
			url
			branch_id
			branch_data {
				...BranchData
				roc
			}
			hero {
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90, layout: FULL_WIDTH)
					}
				}
				heading
				description
				text_position
				cta {
					...CTA
				}
			}
			sponsorship {
				logo {
					url
					gatsbyImageData(quality: 90)
				}
				description
			}
			modular_blocks {
				cards {
					...CardSection
				}
				financing {
					anchor_text
				}
				all_dynamic {
					heading
					description
					dynamic {
						...Dynamic
					}
				}
				rta {
					gray_background
					heading
					subheading
					description
				}
				about {
					image_position
					image_column
					video {
						autoplay
						description
						video {
							title
							button_title
							video_id
						}
						image_overlay {
							image_alt
						}
					}
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					badge {
						...CTA
					}
					heading
					description
					cta {
						...CTA
					}
				}
				nearby {
					heading
					branch_id
					state_only
				}
				feeds {
					anchor_text
				}
				embed {
					embed_id
				}
			}
			seo {
				...SEO
			}
		}

		showroomsByBranch: allShowrooms(filter: { branch_id: { in: $branch_id } }) {
			nodes {
				...Showroom
			}
		}

		showroomsByStoreNumber: allShowrooms(filter: { store_number: { in: $store_numbers } }) {
			nodes {
				...Showroom
			}
		}
	}
`;
